import React, { useMemo, useRef, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Autocomplete,
  Box,
  Button,
  Collapse,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import {
  DESCRIPTION_MAX_LENGTH,
  DiscountType,
  formatEur,
  formatRate,
  htFromTtc,
  invoiceTotals,
  InvoiceItemDraft,
  InvoiceItemErrors,
  lineTotals,
  newItem,
  parseDecimal,
  TITLE_MAX_LENGTH,
  toInputString,
  ttcFromHt,
  UNIT_MAX_LENGTH,
  validateItems,
  VAT_RATES,
} from './invoiceItemsMath';

export interface SavedInvoiceTitle {
  title: string;
  description: string;
  unit?: string;
  unitPriceHT?: string;
  vatRate?: string;
}

export interface InvoiceItemsEditorProps {
  items: InvoiceItemDraft[];
  onChange: (items: InvoiceItemDraft[]) => void;
  savedTitles: SavedInvoiceTitle[];
  onSaveTitle?: (saved: SavedInvoiceTitle) => void;
  disabled?: boolean;
  /** Affiche les erreurs de validation (à passer à true après une tentative d'envoi). */
  showErrors?: boolean;
}

const UNIT_SUGGESTIONS = ['unité', 'heure', 'jour', 'forfait'];
const CUSTOM_VAT = 'custom';
const BORDER = '1px solid #e0e0e0';

const eurAdornment = <InputAdornment position="end">EUR</InputAdornment>;

interface ItemCardProps {
  item: InvoiceItemDraft;
  index: number;
  errors: InvoiceItemErrors;
  savedTitles: SavedInvoiceTitle[];
  onSaveTitle?: (saved: SavedInvoiceTitle) => void;
  disabled?: boolean;
  canRemove: boolean;
  onUpdate: (patch: Partial<InvoiceItemDraft>) => void;
  onRemove: () => void;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  isDragOver: boolean;
  isDragging: boolean;
}

const ItemCard: React.FC<ItemCardProps> = ({
  item,
  index,
  errors,
  savedTitles,
  onSaveTitle,
  disabled,
  canRemove,
  onUpdate,
  onRemove,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isDragOver,
  isDragging,
}) => {
  const [expanded, setExpanded] = useState(true);
  // Saisie TTC en cours : tant que le champ a le focus, on garde le texte tapé
  // (sinon le recalcul HT → TTC écraserait la frappe).
  const [ttcDraft, setTtcDraft] = useState<string | null>(null);
  const [customVat, setCustomVat] = useState(!(VAT_RATES as readonly string[]).includes(item.vatRate));
  const cardRef = useRef<HTMLDivElement>(null);

  const totals = lineTotals(item);
  const htValue = parseDecimal(item.unitPriceHT);
  const ttcDisplay = ttcDraft ?? (htValue === null ? '' : toInputString(ttcFromHt(htValue, item.vatRate)));

  const titleTrimmed = item.title.trim();
  const titleAlreadySaved = savedTitles.some((s) => s.title.trim().toLowerCase() === titleTrimmed.toLowerCase());

  const handleTtcChange = (value: string) => {
    setTtcDraft(value);
    const ttc = parseDecimal(value);
    if (value.trim() === '') onUpdate({ unitPriceHT: '' });
    else if (ttc !== null) onUpdate({ unitPriceHT: toInputString(htFromTtc(ttc, item.vatRate)) });
  };

  const handleVatSelect = (value: string) => {
    if (value === CUSTOM_VAT) {
      setCustomVat(true);
      onUpdate({ vatRate: '' });
    } else {
      setCustomVat(false);
      onUpdate({ vatRate: value });
    }
  };

  const applySaved = (saved: SavedInvoiceTitle) => {
    const patch: Partial<InvoiceItemDraft> = { title: saved.title, description: saved.description ?? '' };
    if (saved.unit !== undefined) patch.unit = saved.unit;
    if (saved.unitPriceHT !== undefined) patch.unitPriceHT = saved.unitPriceHT;
    if (saved.vatRate !== undefined) {
      patch.vatRate = saved.vatRate;
      setCustomVat(!(VAT_RATES as readonly string[]).includes(saved.vatRate));
    }
    onUpdate(patch);
  };

  const setDiscount = (discount: InvoiceItemDraft['discount']) => onUpdate({ discount });

  return (
    <Box
      ref={cardRef}
      onDragOver={onDragOver}
      onDrop={onDrop}
      sx={{
        border: BORDER,
        borderRadius: '8px',
        p: 2,
        bgcolor: 'background.paper',
        opacity: isDragging ? 0.5 : 1,
        borderTop: isDragOver ? '2px solid #1a1a1a' : BORDER,
        transition: 'opacity 120ms',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box
          draggable={!disabled}
          onDragStart={(e) => {
            if (cardRef.current) e.dataTransfer.setDragImage(cardRef.current, 16, 16);
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', item.key);
            onDragStart(e);
          }}
          onDragEnd={onDragEnd}
          aria-label="Déplacer l'article"
          sx={{
            display: 'flex',
            color: 'text.secondary',
            cursor: disabled ? 'default' : 'grab',
            '&:active': { cursor: disabled ? 'default' : 'grabbing' },
          }}
        >
          <DragIndicatorIcon fontSize="small" />
        </Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flexShrink: 0 }}>
          Article {index + 1}
        </Typography>
        {!expanded && (
          <>
            <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {item.title || 'Sans titre'}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, flexShrink: 0 }}>
              {formatEur(totals.ttc)}
            </Typography>
          </>
        )}
        {expanded && <Box sx={{ flex: 1 }} />}
        <IconButton size="small" aria-label="Retirer l'article" onClick={onRemove} disabled={disabled || !canRemove}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
        <IconButton
          size="small"
          aria-label={expanded ? "Replier l'article" : "Déplier l'article"}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Stack>

      <Collapse in={expanded} unmountOnExit>
        <Stack spacing={2} sx={{ mt: 2 }}>
          <Box>
            <Autocomplete<SavedInvoiceTitle, false, false, true>
              freeSolo
              disabled={disabled}
              options={savedTitles}
              getOptionLabel={(o) => (typeof o === 'string' ? o : o.title)}
              inputValue={item.title}
              onInputChange={(_, value, reason) => {
                if (reason === 'input' || reason === 'clear') onUpdate({ title: value.slice(0, TITLE_MAX_LENGTH) });
              }}
              onChange={(_, value) => {
                if (value && typeof value !== 'string') applySaved(value);
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.title}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2">{option.title}</Typography>
                    {option.description && (
                      <Typography variant="caption" color="text.secondary" noWrap component="div">
                        {option.description}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Titre"
                  placeholder="Indiquez un titre"
                  size="small"
                  error={!!errors.title}
                  helperText={errors.title}
                  inputProps={{ ...params.inputProps, maxLength: TITLE_MAX_LENGTH }}
                />
              )}
            />
            {onSaveTitle && titleTrimmed && !titleAlreadySaved && (
              <Button
                size="small"
                variant="text"
                disabled={disabled}
                sx={{ mt: 0.5, px: 0.5, textTransform: 'none' }}
                onClick={() =>
                  onSaveTitle({
                    title: titleTrimmed,
                    description: item.description,
                    unit: item.unit || undefined,
                    unitPriceHT: item.unitPriceHT || undefined,
                    vatRate: item.vatRate || undefined,
                  })
                }
              >
                Enregistrer ce titre
              </Button>
            )}
          </Box>

          <TextField
            label="Détails (optionnel)"
            placeholder="Ajoutez plus de détails"
            size="small"
            multiline
            minRows={2}
            disabled={disabled}
            value={item.description}
            onChange={(e) => onUpdate({ description: e.target.value })}
            inputProps={{ maxLength: DESCRIPTION_MAX_LENGTH }}
            error={!!errors.description}
            helperText={errors.description}
          />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Quantité"
              size="small"
              disabled={disabled}
              value={item.quantity}
              onChange={(e) => onUpdate({ quantity: e.target.value })}
              inputProps={{ inputMode: 'decimal' }}
              error={!!errors.quantity}
              helperText={errors.quantity}
              sx={{ flex: 1 }}
            />
            <Autocomplete
              freeSolo
              disabled={disabled}
              options={UNIT_SUGGESTIONS}
              inputValue={item.unit}
              onInputChange={(_, value) => onUpdate({ unit: value.slice(0, UNIT_MAX_LENGTH) })}
              sx={{ flex: 1 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Unité"
                  size="small"
                  error={!!errors.unit}
                  helperText={errors.unit}
                  inputProps={{ ...params.inputProps, maxLength: UNIT_MAX_LENGTH }}
                />
              )}
            />
            <TextField
              label="Prix unitaire (HT)"
              size="small"
              disabled={disabled}
              value={item.unitPriceHT}
              onChange={(e) => onUpdate({ unitPriceHT: e.target.value })}
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{ endAdornment: eurAdornment }}
              error={!!errors.unitPriceHT}
              helperText={errors.unitPriceHT}
              sx={{ flex: 1.3 }}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Stack direction="row" spacing={1} sx={{ flex: 1 }}>
              <TextField
                select
                label="TVA"
                size="small"
                disabled={disabled}
                value={customVat ? CUSTOM_VAT : item.vatRate}
                onChange={(e) => handleVatSelect(e.target.value)}
                error={!customVat && !!errors.vatRate}
                helperText={!customVat ? errors.vatRate : undefined}
                sx={{ flex: 1 }}
              >
                {VAT_RATES.map((rate) => (
                  <MenuItem key={rate} value={rate}>
                    {formatRate(rate)}
                  </MenuItem>
                ))}
                <MenuItem value={CUSTOM_VAT}>TVA personnalisée</MenuItem>
              </TextField>
              {customVat && (
                <TextField
                  label="Taux"
                  size="small"
                  disabled={disabled}
                  value={item.vatRate}
                  onChange={(e) => onUpdate({ vatRate: e.target.value })}
                  inputProps={{ inputMode: 'decimal', 'aria-label': 'Taux de TVA personnalisé' }}
                  InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                  error={!!errors.vatRate}
                  helperText={errors.vatRate}
                  sx={{ width: 110 }}
                />
              )}
            </Stack>
            <TextField
              label="Prix unitaire (TTC)"
              size="small"
              disabled={disabled}
              value={ttcDisplay}
              onFocus={() => setTtcDraft(ttcDisplay)}
              onBlur={() => setTtcDraft(null)}
              onChange={(e) => handleTtcChange(e.target.value)}
              inputProps={{ inputMode: 'decimal' }}
              InputProps={{ endAdornment: eurAdornment }}
              sx={{ flex: 1 }}
            />
          </Stack>

          {item.discount ? (
            <Box>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  label="Remise"
                  size="small"
                  disabled={disabled}
                  value={item.discount.value}
                  onChange={(e) => setDiscount({ ...item.discount!, value: e.target.value })}
                  inputProps={{ inputMode: 'decimal' }}
                  error={!!errors.discount}
                  helperText={errors.discount}
                  sx={{ flex: 1 }}
                />
                <Select
                  size="small"
                  disabled={disabled}
                  value={item.discount.type}
                  onChange={(e) => setDiscount({ ...item.discount!, type: e.target.value as DiscountType })}
                  inputProps={{ 'aria-label': 'Type de remise' }}
                  sx={{ width: 96 }}
                >
                  <MenuItem value="percentage">%</MenuItem>
                  <MenuItem value="absolute">EUR</MenuItem>
                </Select>
                <IconButton
                  size="small"
                  aria-label="Retirer la remise"
                  disabled={disabled}
                  onClick={() => setDiscount(null)}
                  sx={{ mt: 0.5 }}
                >
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Stack>
              {totals.discountAmount > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Remise de {formatEur(totals.discountAmount)} HT sur cet article
                </Typography>
              )}
            </Box>
          ) : (
            <Box>
              <Button
                size="small"
                variant="text"
                disabled={disabled}
                startIcon={<AddIcon fontSize="small" />}
                onClick={() => setDiscount({ type: 'percentage', value: '' })}
                sx={{ textTransform: 'none', px: 0.5 }}
              >
                Ajouter une remise
              </Button>
            </Box>
          )}

          <Stack direction="row" justifyContent="flex-end" spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Total article
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatEur(totals.ttc)} TTC
            </Typography>
          </Stack>
        </Stack>
      </Collapse>
    </Box>
  );
};

const TotalRow: React.FC<{ label: string; value: number; strong?: boolean }> = ({ label, value, strong }) => (
  <Stack direction="row" justifyContent="space-between" spacing={4}>
    <Typography
      variant="body2"
      sx={{ fontWeight: strong ? 700 : 400 }}
      color={strong ? 'text.primary' : 'text.secondary'}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: strong ? 700 : 500 }}>
      {formatEur(value)}
    </Typography>
  </Stack>
);

const InvoiceItemsEditor: React.FC<InvoiceItemsEditorProps> = ({
  items,
  onChange,
  savedTitles,
  onSaveTitle,
  disabled,
  showErrors = false,
}) => {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  const totals = useMemo(() => invoiceTotals(items), [items]);
  const validation = useMemo(() => validateItems(items), [items]);

  const updateItem = (index: number, patch: Partial<InvoiceItemDraft>) =>
    onChange(items.map((it, i) => (i === index ? { ...it, ...patch } : it)));

  const removeItem = (index: number) => onChange(items.filter((_, i) => i !== index));

  const moveItem = (from: number, to: number) => {
    if (from === to) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    onChange(next);
  };

  const resetDrag = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontSize: 18, fontWeight: 600, mb: 2 }}>
        Produits et services
      </Typography>

      <Stack spacing={1.5}>
        {items.map((item, index) => (
          <ItemCard
            key={item.key}
            item={item}
            index={index}
            errors={showErrors ? (validation.items[index] ?? {}) : {}}
            savedTitles={savedTitles}
            onSaveTitle={onSaveTitle}
            disabled={disabled}
            canRemove={items.length > 1}
            onUpdate={(patch) => updateItem(index, patch)}
            onRemove={() => removeItem(index)}
            onDragStart={() => setDragIndex(index)}
            onDragEnd={resetDrag}
            onDragOver={(e) => {
              if (dragIndex === null) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = 'move';
              if (overIndex !== index) setOverIndex(index);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragIndex !== null) moveItem(dragIndex, index);
              resetDrag();
            }}
            isDragOver={overIndex === index && dragIndex !== null && dragIndex !== index}
            isDragging={dragIndex === index}
          />
        ))}
      </Stack>

      {showErrors && validation.global && (
        <Typography variant="body2" color="error" sx={{ mt: 1 }}>
          {validation.global}
        </Typography>
      )}

      <Button
        variant="text"
        size="small"
        disabled={disabled}
        startIcon={<AddIcon />}
        onClick={() => onChange([...items, newItem()])}
        sx={{ mt: 1.5, textTransform: 'none' }}
      >
        Ajouter un article
      </Button>

      <Divider sx={{ my: 2 }} />

      <Stack spacing={1} sx={{ ml: 'auto', maxWidth: 320 }}>
        <TotalRow label="Total HT" value={totals.totalHT} />
        {totals.vatByRate.map((v) => (
          <TotalRow key={v.rate} label={`TVA ${formatRate(v.rate)}`} value={v.amount} />
        ))}
        <Divider />
        <TotalRow label="Total TTC" value={totals.totalTTC} strong />
      </Stack>
    </Box>
  );
};

export default InvoiceItemsEditor;
